import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  ApprovalTask, DeviceView, HolidayView, OrgAttendanceRow, OrgGroupBy, ProcessingResult,
  PunchView, RegularizationDecisionContext, RegularizationRequestView, RosterView,
  ShiftView, SourceRunView, SourceView, TestResult,
} from '../types'

const BASE = '/v1/attendance'

export function useDevices() {
  return useQuery({
    queryKey: ['attendance', 'devices'],
    queryFn: async () => (await apiClient.get<DeviceView[]>(`${BASE}/devices`)).data,
    // Devices report in every few seconds, so the online indicator needs to keep up.
    refetchInterval: 30_000,
  })
}

export function useRecentPunches(deviceId?: string, limit = 50) {
  return useQuery({
    queryKey: ['attendance', 'punches', deviceId, limit],
    queryFn: async () =>
      (await apiClient.get<PunchView[]>(`${BASE}/punches`, { params: { deviceId, limit } })).data,
    // The live feed is the screen an administrator watches while commissioning a terminal.
    refetchInterval: 10_000,
  })
}

export function useUnmatchedPunches(limit = 50) {
  return useQuery({
    queryKey: ['attendance', 'punches', 'unmatched', limit],
    queryFn: async () =>
      (await apiClient.get<PunchView[]>(`${BASE}/punches/unmatched`, { params: { limit } })).data,
  })
}

export function useSources() {
  return useQuery({
    queryKey: ['attendance', 'sources'],
    queryFn: async () => (await apiClient.get<SourceView[]>(`${BASE}/sources`)).data,
  })
}

export function useSourceMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<SourceView>(`${BASE}/sources`, body)).data,
      onSuccess: invalidate,
    }),
    test: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TestResult>(`${BASE}/sources/${id}/test`)).data,
    }),
    poll: useMutation({
      // Returns a run summary now, not a bare count: "nothing new" and "read three thousand
      // rows that were all already stored" are the two answers somebody debugging an import
      // has to be able to tell apart.
      mutationFn: async (id: string) =>
        (await apiClient.post<SourceRunView>(`${BASE}/sources/${id}/poll`)).data,
      onSuccess: invalidate,
    }),
    replay: useMutation({
      mutationFn: async ({ id, from, to }: { id: string; from: string; to: string }) =>
        (await apiClient.post<SourceRunView>(`${BASE}/sources/${id}/replay`, null, {
          params: { from, to },
        })).data,
      onSuccess: invalidate,
    }),
  }
}

export function useSourceRuns(sourceId: string | null, limit = 25) {
  return useQuery({
    queryKey: ['attendance', 'sources', sourceId, 'runs', limit],
    enabled: sourceId != null,
    queryFn: async () =>
      (await apiClient.get<SourceRunView[]>(`${BASE}/sources/${sourceId}/runs`, {
        params: { limit },
      })).data,
  })
}

// --- regularisation -------------------------------------------------------

export function useMyRegularizations(from?: string, to?: string) {
  return useQuery({
    queryKey: ['attendance', 'regularizations', 'my', from, to],
    queryFn: async () =>
      (await apiClient.get<RegularizationRequestView[]>(`${BASE}/regularizations/my`, {
        params: { from, to },
      })).data,
  })
}

export function useOpenRegularizations(from?: string, to?: string, employeeId?: string) {
  return useQuery({
    queryKey: ['attendance', 'regularizations', 'open', from, to, employeeId],
    queryFn: async () =>
      (await apiClient.get<RegularizationRequestView[]>(`${BASE}/regularizations`, {
        params: { from, to, employeeId: employeeId === '' ? undefined : employeeId },
      })).data,
  })
}

/**
 * The claim and the evidence in one request.
 *
 * Deliberately not two queries. An approver deciding whether somebody really worked until
 * 18:30 needs the punches the terminals actually recorded next to the time being claimed,
 * and a screen that can render the claim while the evidence is still loading is a screen
 * people approve from.
 */
export function useRegularizationContext(id: string | null) {
  return useQuery({
    queryKey: ['attendance', 'regularizations', id, 'context'],
    enabled: id != null,
    queryFn: async () =>
      (await apiClient.get<RegularizationDecisionContext>(`${BASE}/regularizations/${id}/context`)).data,
  })
}

export function useRegularizationMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance'] })

  return {
    submit: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<RegularizationRequestView>(`${BASE}/regularizations`, body)).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
        (await apiClient.post<RegularizationRequestView>(`${BASE}/regularizations/${id}/cancel`, {
          reason,
        })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- approvals ------------------------------------------------------------
// One inbox for every approvable thing. Approving a correction goes through the same
// endpoint as approving leave, because the workflow engine is what decides who may act.

export function useApprovalTasks(entityKey?: string) {
  return useQuery({
    queryKey: ['approvals', 'tasks', entityKey],
    queryFn: async () =>
      (await apiClient.get<ApprovalTask[]>('/v1/approvals/tasks', { params: { entityKey } })).data,
  })
}

export function useApprovalMutations() {
  const queryClient = useQueryClient()
  // A decision moves the business record as well as the task, so all three caches are
  // stale afterwards — the inbox, the correction it decided, and leave if that is what it was.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['approvals'] })
    void queryClient.invalidateQueries({ queryKey: ['attendance'] })
    void queryClient.invalidateQueries({ queryKey: ['leave'] })
  }

  return {
    approve: useMutation({
      mutationFn: async ({ taskId, comment }: { taskId: string; comment?: string }) =>
        (await apiClient.post<ApprovalTask>(`/v1/approvals/tasks/${taskId}/approve`, { comment })).data,
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: async ({ taskId, comment }: { taskId: string; comment?: string }) =>
        (await apiClient.post<ApprovalTask>(`/v1/approvals/tasks/${taskId}/reject`, { comment })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- reports --------------------------------------------------------------

/**
 * Totals grouped by where people actually were.
 *
 * The backend groups through the assignment pinned on each attendance day, not through the
 * employee's current one — so a March report does not change when somebody transfers in
 * April.
 */
export function useOrgAttendanceReport(from: string, to: string, groupBy: OrgGroupBy) {
  return useQuery({
    queryKey: ['attendance', 'reports', 'by-org', from, to, groupBy],
    queryFn: async () =>
      (await apiClient.get<OrgAttendanceRow[]>(`${BASE}/reports/by-org`, {
        params: { from, to, groupBy },
      })).data,
    placeholderData: (previous) => previous,
  })
}

export function useDeviceMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance', 'devices'] })

  return {
    approve: useMutation({
      mutationFn: async (id: string) => (await apiClient.post<DeviceView>(`${BASE}/devices/${id}/approve`)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<DeviceView>(`${BASE}/devices/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    // Disabled, not deleted: every punch on file names the device it came from, and a removed
    // row would leave a month of attendance with no provenance.
    deactivate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<DeviceView>(`${BASE}/devices/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    queueCommand: useMutation({
      mutationFn: async ({ id, type }: { id: string; type: string }) =>
        (await apiClient.post<DeviceView>(`${BASE}/devices/${id}/commands`, null, { params: { type } })).data,
      onSuccess: invalidate,
    }),
    pushUsers: useMutation({
      mutationFn: async (id: string) => (await apiClient.post<number>(`${BASE}/devices/${id}/push-users`)).data,
      onSuccess: invalidate,
    }),
  }
}

export function useShifts() {
  return useQuery({
    queryKey: ['attendance', 'shifts'],
    queryFn: async () => (await apiClient.get<ShiftView[]>(`${BASE}/shifts`)).data,
  })
}

export interface RosterQuery {
  q?: string
  shiftId?: string
  page: number
  size: number
}

export function useRosters(query: RosterQuery) {
  return useQuery({
    queryKey: ['attendance', 'rosters', query],
    queryFn: async () =>
      (await apiClient.get<PageResponse<RosterView>>(`${BASE}/rosters`, {
        params: {
          q: query.q === '' ? undefined : query.q,
          shiftId: query.shiftId === '' ? undefined : query.shiftId,
          page: query.page,
          size: query.size,
        },
      })).data,
    // Paging through a roster should not blank the table between pages.
    placeholderData: (previous) => previous,
  })
}

export function useHolidays(year?: number) {
  return useQuery({
    queryKey: ['attendance', 'holidays', year ?? 'current'],
    queryFn: async () =>
      (await apiClient.get<HolidayView[]>(`${BASE}/holidays`, { params: { year } })).data,
  })
}

/**
 * The calendar writes that did not exist.
 *
 * <p>Holidays were read-only: the table was populated by a migration and read by the
 * attendance processor, so every installation worked to whatever calendar was seeded — and a
 * holiday the processor does not know about is a day the whole company is marked absent on.
 */
export function useHolidayMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance'] })

  return {
    save: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<HolidayView>(`${BASE}/holidays/${id}`, body)).data
          : (await apiClient.post<HolidayView>(`${BASE}/holidays`, body)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/holidays/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

export function useRosterMutations() {
  const queryClient = useQueryClient()
  // Shift counts change when people are assigned, so both lists are refreshed together.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attendance'] })

  return {
    createShift: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ShiftView>(`${BASE}/shifts`, body)).data,
      onSuccess: invalidate,
    }),
    // A shift's grace, break and thresholds are what the whole attendance calculation turns
    // on. Until this existed, a typo in any of them was permanent.
    updateShift: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<ShiftView>(`${BASE}/shifts/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    deactivateShift: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.post(`${BASE}/shifts/${id}/deactivate`)
      },
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<RosterView[]>(`${BASE}/rosters`, body)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/rosters/${id}`)
      },
      onSuccess: invalidate,
    }),
    reprocess: useMutation({
      mutationFn: async ({ from, to }: { from: string; to: string }) =>
        (await apiClient.post<ProcessingResult>(`${BASE}/processing/run`, null, { params: { from, to } })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- Phase 7 part 5: the daily grid ---

export interface DailyRow {
  employeeId: string
  employeeCode: string
  employeeName: string
  departmentId: string | null
  status: string
  firstIn: string | null
  lastOut: string | null
  workedMinutes: number
  lateMinutes: number
  overtimeMinutes: number
  source: string | null
  /** False when nothing has been processed for this person that day — which is not absent. */
  recorded: boolean
}

export function useDailyAttendance(params: {
  date?: string; entityId?: string; departmentId?: string; q?: string
  page?: number; size?: number
}) {
  return useQuery({
    queryKey: ['attendance', 'days', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<DailyRow>>('/v1/attendance/days', {
        params: {
          date: params.date || undefined,
          entityId: params.entityId || undefined,
          departmentId: params.departmentId || undefined,
          q: params.q || undefined,
          page: params.page,
          size: params.size,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}
